import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useContext, useState, useRef, useEffect } from "react";
import Toast from "../Toast/Toast";
import ReCAPTCHA from "react-google-recaptcha";
import { POST } from "../../service/CRUDService";
import AppConstants from "../../configs/AppConstants";
import EntityConstants from "../../configs/EntityConstants";
import AuthContext from "../../store/auth-context";
import { useTranslation } from "react-i18next";

export default function ForgotPasswordModal({
  isModalOpen,
  certificate_header,
  certificate_description,
  testId = "forgot-password-modal",
}) {
  const emailInputId = `${testId}-email`;
  const authContext = useContext(AuthContext);
  const { t } = useTranslation();

  const InputBorderRadius = "0.5rem";
  const [email, setEmail] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const recaptchaRef = useRef(null);

  useEffect(() => {
    if (isModalOpen) {
      setIsSubmitting(false);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setCaptchaValue(null);
      }
    }
  }, [isModalOpen]);

  function closeModal() {
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
      setCaptchaValue(null);
    }
    setEmail("");
    setIsEmailValid(true);
    authContext.forgotPasswordHandler();
  }

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    const emailInput = e.target.value.trim();
    setEmail(emailInput);
    setIsEmailValid(validateEmail(emailInput));
  };

  const forgotPassword = async () => {
    if (isSubmitting) {
      return;
    }

    if (!isEmailValid) {
      Toast({
        type: "error",
        title: t("authPages:login_failed"),
        message: t("authPages:valid_email_required"),
      });
      return;
    }

    if (!captchaValue) {
      Toast({
        type: "error",
        title: t("authPages:captcha_error"),
        message: t("authPages:captcha_incomplete"),
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await POST(EntityConstants.POST_FORGOT_PASSWORD, {
        email,
      });

      const data = await response.json();

      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setCaptchaValue(null);
      }

      if (response.ok) {
        Toast({
          type: "success",
          title: t("authPages:success"),
          message: data.message,
        });
        closeModal();
      } else {
        Toast({
          type: "error",
          title: t("authPages:error"),
          message: data.message,
        });
      }
    } catch (error) {
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setCaptchaValue(null);
      }

      console.error("Failed to send email", error);
      Toast({
        type: "error",
        title: t("authPages:error"),
        message: t("authPages:email_send_error"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={isSubmitting ? () => {} : closeModal}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              id={`${testId}-backdrop`}
              className="fixed inset-0 z-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
            />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in duration-200 transform"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="transition ease-out transform"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  id={testId}
                  className="relative z-10 w-full max-w-sm transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
                >
                  <button
                    type="button"
                    id={`${testId}-close`}
                    disabled={isSubmitting}
                    className="absolute top-3 right-3 text-gray-400 hover:text-gray-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={closeModal}
                  >
                    <span className="sr-only">Close</span>
                    &#10005;
                  </button>

                  <Dialog.Title
                    as="h3"
                    className="text-4xl pt-3 pb-4 font-medium leading-6 text-gray-900 flex"
                  >
                    {certificate_header}
                  </Dialog.Title>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      forgotPassword();
                    }}
                  >
                    <div className="flex flex-col gap-2 mt-2">
                      <div>
                        <input
                          type="email"
                          name="email"
                          id={emailInputId}
                          required
                          placeholder={t("authPages:enter_email")}
                          autoComplete="true"
                          className="rounded-md w-full h-12 focus:outline-none border border-cfSignInSignUpInputBorder text-base text-cfSignInSignUpInputPlaceholderText"
                          style={{ borderRadius: InputBorderRadius }}
                          value={email}
                          onChange={handleEmailChange}
                        />
                        {!isEmailValid && (
                          <p className="text-red-500 text-sm mt-1">
                            {t("authPages:valid_email_required")}
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-cfSignInSignUpIntroMessageText py-2 text-base text-gray-500 ml-2">
                          {certificate_description}
                        </p>
                      </div>

                      <div className="flex justify-center my-2">
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          sitekey={AppConstants.GOOGLE_CAPTCHA_SITE_KEY}
                          onChange={handleCaptchaChange}
                        />
                      </div>
                    </div>

                    <div className="mt-4 flex justify-center">
                      <button
                        type="submit"
                        id={`${testId}-submit`}
                        disabled={
                          !isEmailValid || !captchaValue || isSubmitting
                        }
                        aria-busy={isSubmitting}
                        className={`inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl px-5 py-1 text-base font-semibold text-cfSecondary transition-colors duration-200 ${
                          isSubmitting
                            ? "cursor-wait bg-cfButton"
                            : "bg-cfButton hover:bg-cfPrimaryDark"
                        }`}
                      >
                        {isSubmitting && (
                          <svg
                            className="h-4 w-4 shrink-0 animate-spin text-cfSecondary"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                        )}
                        {isSubmitting
                          ? t("authPages:sending_reset_link")
                          : t("authPages:submit")}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
